import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import { uniqBy } from "lodash";
import { message, Modal } from "antd";
import { cancelSplitOrder } from "@/services/api/order";
import BatchCancelSplitOrderResultModal, {
    BatchCancelSplitOrderResult,
} from "../components/BatchCancelSplitOrderResultModal";
import { ModalStore } from "@/hooks/Modal/ModalStore";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import GlobalStore from "@/store";

async function batchCancelSplitOrders(
    orders: OrderDTO[],
    modalStore: ModalStore,
    globalStore: typeof GlobalStore,
    onSuccess?: () => void,
    shopId?: number,
) {
    const results: BatchCancelSplitOrderResult[] = [];

    let canCancelSplitOrders: OrderDTO[] = [];

    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];

        if (order.mergeSplitStatus !== OrderMergeSplitStatus.SPLIT_CHILD) {
            continue;
        }

        if (!order.parentId) {
            continue;
        }

        const hasDropShipping = !!order.retailerDropShippingInfo;

        if (hasDropShipping) {
            results.push({
                originalId: order.originalId,
                error: "已提交给供货商的代发订单，不允许取消拆分",
            });

            continue;
        }

        canCancelSplitOrders.push(order);
    }

    // 真实的可取消拆分的子订单数量
    const realCount = canCancelSplitOrders.length;

    if (!realCount) {
        return Modal.error({
            title: "请至少先选择一个拆分子订单再操作",
        });
    }

    // 按照parentId去重
    canCancelSplitOrders = uniqBy(canCancelSplitOrders, "parentId");

    Modal.confirm({
        title: `已选择${realCount}个拆分子订单`,
        content:
            "即将取消订单拆分，取消后会将所有子订单删除，订单将还原回独立订单状态。拆分子订单的备注、操作日志都会被删除",
        async onOk() {
            const closer = message.open({
                type: "loading",
                content: "正在取消拆分订单",
                key: "batchCancelSplitOrder",
                duration: 0,
            });

            let successCount = 0;

            for (let i = 0; i < canCancelSplitOrders.length; i++) {
                const order = canCancelSplitOrders[i];

                message.open({
                    type: "loading",
                    content: `正在取消拆分订单 ${i + 1}/${canCancelSplitOrders.length}`,
                    key: "batchCancelSplitOrder",
                    duration: 0,
                });

                try {
                    await cancelSplitOrder(order.shopId, order.parentId, true);

                    successCount++;
                } catch (error) {
                    results.push({
                        originalId: order.originalId,
                        error: error?.message,
                    });
                }
            }

            closer();

            if (results.length) {
                modalStore.openModal(BatchCancelSplitOrderResultModal, {
                    successCount,
                    results,
                });
            } else {
                message.success("批量取消拆分成功");
            }

            onSuccess?.();

            try {
                const shop = globalStore.shopMapping.get(shopId);

                collectEvent(DataTrackEvent.CANCEL_SPLIT_ORDER, {
                    order_count: canCancelSplitOrders.length,
                    shop_id: shop ? shop.id : null,
                    shop_name: shop ? shop.name : null,
                    shop_type: shop ? ShopTypeNameMap[shop.type] : null,
                    companyId: shop ? null : globalStore.companyId,
                });
            } catch {}
        },
    });
}

export default batchCancelSplitOrders;
